// data.tsx — API endpoint specs, mock responses, dashboard data, AI/agent content.
import { GENERATED_GROUPS } from './data.generated';

// Placeholder demo keys — NOT real credentials. Deliberately low-entropy/obvious
// so secret scanners don't flag them. Only used as sample values in the playground UI.
const TEST_KEY = 'melio_secret_staging01_EXAMPLE0000000000';
const LIVE_KEY = 'melio_secret_production_EXAMPLE0000000000';

// ---- Endpoint catalogue -------------------------------------------------
// Entities/Accounts/Payments/Webhooks/Lookup are generated from payouts.api.external.yaml by
// scripts/generate-api-reference.js (see data.generated.ts). The in-app reference page that used
// to render the full catalogue is gone — docs live at /docs — so only the worked payment-create
// example below is still read from it.

// The Payments group's "Create a payment" endpoint — looked up by its REST shape (POST .../payments)
// rather than the generator's auto-derived id, so a future id-scheme change in the generator can't
// silently break this. Used by home.tsx for its worked payment-create example.
const PAYMENT_CREATE_ENDPOINT = (GENERATED_GROUPS as any[])
  .find((g) => g.id === 'payments')
  .endpoints.find((e: any) => e.method === 'POST' && e.path.endsWith('/payments'));

const PAYMENT_CREATE_SPEC = PAYMENT_CREATE_ENDPOINT.spec;

const RES = {
  payment: PAYMENT_CREATE_ENDPOINT.response,
  paymentEligibilityError: PAYMENT_CREATE_ENDPOINT.errorResponse,
};

const PAYMENT_STATES = [
  { key: 'scheduled', label: 'Scheduled', desc: 'Created and queued for its scheduled date.' },
  { key: 'in_progress', label: 'In progress', desc: 'Funds are being debited from the funding source.' },
  { key: 'sent', label: 'Sent', desc: 'ACH transfer dispatched or paper check mailed.' },
  { key: 'completed', label: 'Completed', desc: 'Delivered to the recipient. Terminal state.' },
];

// ---- Dashboard data -----------------------------------------------------
const DASH = {
  businesses: [
    {
      id: 'org_9688713',
      name: 'Sunrise Bakery',
      initials: 'SB',
      color: 'var(--decorative-2)',
      eligible: true,
      payments: 24,
      volume: 4820000,
    },
    {
      id: 'org_2k9p4x',
      name: 'Cedar & Co. Realty',
      initials: 'CC',
      color: 'var(--decorative-4)',
      eligible: true,
      payments: 11,
      volume: 9210000,
    },
    { id: 'org_7w1m8z', name: 'Harbor Logistics', initials: 'HL', color: 'var(--decorative-5)', eligible: false, payments: 0, volume: 0 },
    {
      id: 'org_5q3n6t',
      name: 'Bright Dental Group',
      initials: 'BD',
      color: 'var(--decorative-3)',
      eligible: true,
      payments: 7,
      volume: 1540000,
    },
  ],
  payments: [
    {
      id: 'pymnt_3x9k2v',
      payee: 'Flour Power Supply Co.',
      biz: 'Sunrise Bakery',
      amount: 125000,
      method: 'ach',
      status: 'completed',
      date: '2026-05-28',
    },
    {
      id: 'pymnt_8h2j5n',
      payee: 'Cedar & Co. Realty',
      biz: 'Cedar & Co. Realty',
      amount: 480000,
      method: 'check',
      status: 'sent',
      date: '2026-05-30',
    },
    { id: 'pymnt_1f7d9k', payee: 'PG&E', biz: 'Sunrise Bakery', amount: 32400, method: 'ach', status: 'in_progress', date: '2026-06-01' },
    {
      id: 'pymnt_4m6b3p',
      payee: 'Bay Area Linen',
      biz: 'Bright Dental Group',
      amount: 18900,
      method: 'ach',
      status: 'scheduled',
      date: '2026-06-05',
    },
    {
      id: 'pymnt_9z2x7q',
      payee: 'Westfield Property Mgmt',
      biz: 'Cedar & Co. Realty',
      amount: 920000,
      method: 'check',
      status: 'scheduled',
      date: '2026-06-07',
    },
    {
      id: 'pymnt_6c1v8w',
      payee: 'Acme Packaging',
      biz: 'Sunrise Bakery',
      amount: 54200,
      method: 'ach',
      status: 'failed',
      date: '2026-05-22',
    },
  ],
  keys: [
    {
      id: 'k1',
      label: 'Production key',
      prefix: 'melio_secret_production_EX',
      env: 'live',
      created: '2026-05-21',
      lastUsed: '2 minutes ago',
      note: 'Vault: prod/melio',
    },
    {
      id: 'k2',
      label: 'Sandbox key',
      prefix: 'melio_secret_staging01_EX',
      env: 'test',
      created: '2026-05-21',
      lastUsed: '18 seconds ago',
      note: 'Local .env',
    },
  ],
  logs: [
    { method: 'POST', path: '/v2/businesses/org_9688713/payments', status: 201, ms: 412, time: '09:31:20', id: 'req_7Hk2', env: 'test' },
    {
      method: 'GET',
      path: '/v2/businesses/org_9688713/payments/pymnt_3x9k2v',
      status: 200,
      ms: 88,
      time: '09:31:22',
      id: 'req_9Lm4',
      env: 'test',
    },
    {
      method: 'POST',
      path: '/v2/businesses/org_9688713/delivery-methods',
      status: 201,
      ms: 356,
      time: '09:30:55',
      id: 'req_2Qp8',
      env: 'test',
    },
    { method: 'POST', path: '/v2/businesses/org_7w1m8z/payments', status: 409, ms: 121, time: '09:29:40', id: 'req_5Rt1', env: 'test' },
    { method: 'GET', path: '/v2/businesses', status: 200, ms: 64, time: '09:28:10', id: 'req_3Vb6', env: 'test' },
    {
      method: 'POST',
      path: '/v2/businesses/org_9688713/funding-sources',
      status: 201,
      ms: 298,
      time: '09:27:33',
      id: 'req_8Nw2',
      env: 'test',
    },
    { method: 'PATCH', path: '/v2/businesses/org_7w1m8z', status: 200, ms: 142, time: '09:26:02', id: 'req_1Cf9', env: 'test' },
    { method: 'POST', path: '/v2/businesses/org_9688713/payments', status: 422, ms: 73, time: '09:24:51', id: 'req_4Dg7', env: 'test' },
  ],
  events: [
    { type: 'payment.completed', id: 'evt_9k2', payment: 'pymnt_3x9k2v', status: 'delivered', code: 200, time: '09:31:48', attempts: 1 },
    { type: 'payment.sent', id: 'evt_8h5', payment: 'pymnt_8h2j5n', status: 'delivered', code: 200, time: '09:30:12', attempts: 1 },
    { type: 'payment.in_progress', id: 'evt_1f7', payment: 'pymnt_1f7d9k', status: 'delivered', code: 200, time: '09:29:01', attempts: 1 },
    { type: 'payment.failed', id: 'evt_6c1', payment: 'pymnt_6c1v8w', status: 'failed', code: 500, time: '09:22:18', attempts: 4 },
    { type: 'business.eligibility_updated', id: 'evt_7w1', payment: '—', status: 'delivered', code: 200, time: '09:20:44', attempts: 1 },
  ],
};

function fmtMoney(cents: number): string {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

type StatusKind = 'success' | 'informative' | 'warning' | 'neutral' | 'critical';

function statusKind(s: string): StatusKind {
  return (
    (
      {
        completed: 'success',
        sent: 'informative',
        in_progress: 'warning',
        scheduled: 'neutral',
        failed: 'critical',
        canceled: 'neutral',
      } as Record<string, StatusKind>
    )[s] ?? 'neutral'
  );
}

function statusLabel(s: string): string {
  return (
    (
      {
        completed: 'Completed',
        sent: 'Sent',
        in_progress: 'In progress',
        scheduled: 'Scheduled',
        failed: 'Failed',
        canceled: 'Canceled',
      } as Record<string, string>
    )[s] ?? s
  );
}

export { TEST_KEY, LIVE_KEY, RES, PAYMENT_CREATE_SPEC, PAYMENT_STATES, DASH, fmtMoney, statusKind, statusLabel };
